import { useState, useEffect } from 'react'
import { Lock, Calendar, Hash, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import { pagarmeService } from '@/services/pagarme.service'
import { paymentService } from '@/services/payment.service'
import type { CardData } from '@/types/payment'
import { formatCurrency } from '@/lib/utils'
import { trackUmami } from '@/lib/tracking'

interface SandboxCardScenario {
  id: string
  label: string
  description: string
  card_number: string
}

const SANDBOX_CARD_SCENARIOS: SandboxCardScenario[] = [
  {
    id: 'approved',
    label: 'Aprovado',
    description: '4000000000000010',
    card_number: '4000000000000010',
  },
  {
    id: 'not-authorized',
    label: 'Não autorizado',
    description: '4000000000000028',
    card_number: '4000000000000028',
  },
  {
    id: 'processing-approved',
    label: 'Proc. > Aprovado',
    description: '4000000000000036',
    card_number: '4000000000000036',
  },
  {
    id: 'processing-failed',
    label: 'Proc. > Falha',
    description: '4000000000000044',
    card_number: '4000000000000044',
  },
]

function isSlacLocalHost(hostname: string): boolean {
  return hostname === 'slac.local' || hostname.endsWith('.slac.local')
}

function getDefaultSandboxExpiration(): string {
  const expirationDate = new Date()
  expirationDate.setFullYear(expirationDate.getFullYear() + 2)
  const month = String(expirationDate.getMonth() + 1).padStart(2, '0')
  const year = String(expirationDate.getFullYear() % 100).padStart(2, '0')
  return `${month}/${year}`
}

interface CreditCardFormProps {
  orderId: string
  amount: number
  installmentsConfig: {
    max: number
    min: number
    interest_free: number
    interest_rate?: number
  }
  onSuccess?: (data: any) => void
  onError?: (error: any) => void
}

export function CreditCardForm({
  orderId,
  amount,
  installmentsConfig,
  onSuccess,
  onError,
}: CreditCardFormProps) {
  const [loading, setLoading] = useState(false)
  const [cardData, setCardData] = useState<CardData>({
    card_number: '',
    card_holder_name: '',
    card_expiration_date: '',
    card_cvv: '',
  })
  const [installments, setInstallments] = useState('1')
  const [cardBrand, setCardBrand] = useState<string | null>(null)
  const [billingAddress, setBillingAddress] = useState({ line_1: '', zip_code: '', city: '', state: '' })
  const { toast } = useToast()
  const showSandboxAutofill =
    typeof window !== 'undefined' && isSlacLocalHost(window.location.hostname)

  // Detectar bandeira do cartão
  useEffect(() => {
    if (cardData.card_number.length >= 6) {
      const brand = pagarmeService.getCardBrand(cardData.card_number)
      setCardBrand(brand)
    } else {
      setCardBrand(null)
    }
  }, [cardData.card_number])

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = pagarmeService.formatCardNumber(e.target.value)
    setCardData({ ...cardData, card_number: formatted })
  }

  const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = pagarmeService.formatExpirationDate(e.target.value)
    setCardData({ ...cardData, card_expiration_date: formatted })
  }

  const calculateInstallmentAmount = (installmentNum: number): number => {
    const baseAmount = amount / installmentNum

    if (installmentNum <= installmentsConfig.interest_free) {
      return baseAmount
    }

    const rate = installmentsConfig.interest_rate || 0
    const interest = 1 + (rate / 100)
    return baseAmount * interest
  }

  const getInstallmentOptions = () => {
    const options = []
    for (let i = installmentsConfig.min; i <= installmentsConfig.max; i++) {
      const installmentAmount = calculateInstallmentAmount(i)
      const isInterestFree = i <= installmentsConfig.interest_free

      options.push({
        value: i,
        label: i === 1
          ? `${formatCurrency(amount)} à vista`
          : `${i}x de ${formatCurrency(installmentAmount)} ${isInterestFree ? 'sem juros' : 'com juros'}`,
      })
    }
    return options
  }

  const applySandboxScenario = (scenario: SandboxCardScenario) => {
    setCardData({
      card_number: pagarmeService.formatCardNumber(scenario.card_number),
      card_holder_name: 'TESTE SLAC',
      card_expiration_date: getDefaultSandboxExpiration(),
      card_cvv: '123',
    })
    setInstallments('1')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validar número do cartão
      if (!pagarmeService.validateCardNumber(cardData.card_number)) {
        throw new Error('Número do cartão inválido')
      }

      // Validar validade
      const [month, year] = cardData.card_expiration_date.split('/')
      const currentYear = new Date().getFullYear() % 100
      const currentMonth = new Date().getMonth() + 1

      if (!month || !year || parseInt(month) > 12 || parseInt(month) < 1) {
        throw new Error('Data de validade inválida')
      }

      if (parseInt(year) < currentYear || (parseInt(year) === currentYear && parseInt(month) < currentMonth)) {
        throw new Error('Cartão expirado')
      }

      // Validar CVV
      if (cardData.card_cvv.length < 3 || cardData.card_cvv.length > 4) {
        throw new Error('CVV inválido')
      }

      if (!billingAddress.line_1.trim() || !/^\d{8}$/.test(billingAddress.zip_code) || !billingAddress.city.trim() || !/^[A-Z]{2}$/.test(billingAddress.state)) {
        throw new Error('Preencha o endereço de cobrança, incluindo CEP e UF.')
      }

      // Tokenizar cartão (client-side via Pagar.me)
      const cardHash = await pagarmeService.tokenizeCard(cardData)

      trackUmami('Tentou Pagamento Cartao', {
        parcelas: parseInt(installments),
        bandeira: cardBrand || 'desconhecida',
        valor: amount,
      })

      // Processar pagamento
      const result = await paymentService.processCreditCard({
        order_id: orderId,
        card_hash: cardHash,
        installments: parseInt(installments),
        save_card: false,
        billing_address: billingAddress,
      })

      if (result.status === 'paid') {
        toast({ title: 'Pagamento aprovado!', description: 'Seu pedido foi confirmado' })
        trackUmami('Pagamento Aprovado Cartao', {
          parcelas: parseInt(installments), bandeira: cardBrand || 'desconhecida', valor: amount,
        })
      } else if (['failed', 'canceled', 'refunded', 'chargedback', 'expired'].includes(result.status)) {
        throw new Error('Pagamento não aprovado. Confira os dados ou escolha outra forma de pagamento.')
      } else {
        toast({ title: 'Pagamento em análise', description: 'Aguarde a confirmação antes de tentar novamente.' })
      }

      onSuccess?.(result)
    } catch (error: any) {
      console.error('Erro no pagamento:', error)

      trackUmami('Pagamento Negado', {
        metodo: 'Cartao de Credito',
        erro: error.message || 'Erro desconhecido',
        valor: amount,
      })

      toast({
        title: 'Erro no pagamento',
        description: error.message || 'Tente novamente ou use outro cartão',
        variant: 'destructive',
      })

      onError?.(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {showSandboxAutofill && (
            <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Sandbox Pagar.me (.slac.local)
              </p>
              <div className="flex flex-wrap gap-2">
                {SANDBOX_CARD_SCENARIOS.map((scenario) => (
                  <Button
                    key={scenario.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                    onClick={() => applySandboxScenario(scenario)}
                    title={scenario.description}
                  >
                    {scenario.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Número do Cartão */}
          <div className="space-y-2">
            <Label htmlFor="card_number">Número do cartão</Label>
            <div className="relative">
              <Input
                id="card_number"
                name="card_number"
                placeholder="0000 0000 0000 0000"
                value={cardData.card_number}
                onChange={handleCardNumberChange}
                maxLength={19}
                autoComplete="cc-number"
                inputMode="numeric"
                required
              />
              {cardBrand && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <span className="text-xs font-semibold uppercase text-muted-foreground">
                    {cardBrand}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Nome no Cartão */}
          <div className="space-y-2">
            <Label htmlFor="card_holder_name">Nome no cartão</Label>
            <Input
              id="card_holder_name"
              name="card_holder_name"
              placeholder="NOME COMO ESTÁ NO CARTÃO"
              value={cardData.card_holder_name}
              onChange={(e) => setCardData({ ...cardData, card_holder_name: e.target.value.toUpperCase() })}
              autoComplete="cc-name"
              required
            />
          </div>

          {/* Validade e CVV */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="card_expiration_date" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Validade
              </Label>
              <Input
                id="card_expiration_date"
                name="card_expiration_date"
                placeholder="MM/AA"
                value={cardData.card_expiration_date}
                onChange={handleExpirationChange}
                maxLength={5}
                autoComplete="cc-exp"
                inputMode="numeric"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="card_cvv" className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                CVV
              </Label>
              <Input
                id="card_cvv"
                name="card_cvv"
                type="tel"
                placeholder="123"
                value={cardData.card_cvv}
                onChange={(e) => setCardData({ ...cardData, card_cvv: e.target.value.replace(/\D/g, '') })}
                maxLength={4}
                autoComplete="cc-csc"
                inputMode="numeric"
                required
              />
            </div>
          </div>

          <Separator />

          {/* Parcelamento */}
          <div className="space-y-2">
            <Label htmlFor="installments">Parcelamento</Label>
            <Select value={installments} onValueChange={setInstallments}>
              <SelectTrigger id="installments">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getInstallmentOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Endereço de cobrança</legend>
            <div className="space-y-2">
              <Label htmlFor="billing_line_1">Rua, número e bairro</Label>
              <Input id="billing_line_1" autoComplete="address-line1" required maxLength={255}
                value={billingAddress.line_1} onChange={(e) => setBillingAddress({ ...billingAddress, line_1: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="billing_zip_code">CEP</Label>
                <Input id="billing_zip_code" autoComplete="postal-code" inputMode="numeric" required minLength={8} maxLength={8}
                  value={billingAddress.zip_code} onChange={(e) => setBillingAddress({ ...billingAddress, zip_code: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing_state">UF</Label>
                <Input id="billing_state" autoComplete="address-level1" required minLength={2} maxLength={2}
                  value={billingAddress.state} onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value.replace(/[^a-z]/gi, '').toUpperCase() })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing_city">Cidade</Label>
              <Input id="billing_city" autoComplete="address-level2" required maxLength={120}
                value={billingAddress.city} onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })} />
            </div>
          </fieldset>

          {/* Segurança */}
          <div className="rounded-lg bg-green-50 p-3 dark:bg-green-950/20">
            <div className="flex items-center gap-2 text-sm text-green-800 dark:text-green-400">
              <Lock className="h-4 w-4" />
              <span>Seus dados estão protegidos e criptografados</span>
            </div>
          </div>

          {/* Botão Submit */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processando pagamento...
              </>
            ) : (
              <>
                Pagar {formatCurrency(amount)}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
